import { useState, useEffect } from 'preact/hooks';
import type { Invitation } from '../types';
import { getAuthHeaders } from '../utils/auth';

/**
 * Hook to manage team invitations
 *
 * Fetches and manages pending invitations for the current user.
 * Used by Header component to show invitation badge.
 *
 * @returns Object with invitations array, loading state, error, refresh function, and pendingCount
 */
export function useInvitations() {
  const [invitations, setInvitations] = useState<Invitation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadInvitations = async () => {
    try {
      const authHeaders = await getAuthHeaders();
      if (!authHeaders['Authorization']) {
        setInvitations([]);
        setLoading(false);
        return;
      }

      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:4000/api/v1';
      const response = await fetch(`${apiUrl}/invitations`, {
        headers: authHeaders
      });

      if (!response.ok) {
        if (response.status === 401) {
          // User not authenticated, just return empty list
          setInvitations([]);
          setLoading(false);
          return;
        }
        throw new Error('Failed to load invitations');
      }

      const data = await response.json();
      setInvitations(data.invitations || []);
    } catch (err) {
      console.error('Error loading invitations:', err);
      setError(err instanceof Error ? err.message : 'Failed to load invitations');
      setInvitations([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadInvitations();
  }, []);

  return {
    invitations,
    loading,
    error,
    refresh: loadInvitations,
    pendingCount: invitations.filter(inv => inv.status === 'pending').length
  };
}
